import React from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { sensorFullNameKey, colors } from 'commons/src/constants';
import { Rating } from 'commons/src/models/commonEnums';
import {
    calculateSpacesByRating,
    calculatePeakRanges,
    getSpacesWithIssues,
    sensorRating,
    formatRangeText,
    formatRating,
    getStatusColor,
    sensorShowsRatingLabels,
    DeviceMeasurement,
} from 'commons/src/sensorRatingUtils';
import { IaqInsightData, PDFContext, ReportSensorThresholds } from '../../../../../models/common';
import {
    formatSensorValue,
    formatSensorUnit,
    getSensorIconSource,
    getStatusPillColors,
    SpaceMeasurementDataColumns,
} from '../utils/pdfHelpers';
import iaqInsight2PdfStyles from '../utils/pdfStyles';
import IaqInsight2DataTable from './IaqInsight2DataTable';

type IaqInsight2SensorSectionProps = {
    translationFunction: PDFContext;
    sensorType: string;
    sensorThresholdConfiguration: ReportSensorThresholds;
    sensorReportData: IaqInsightData;
    spaceMeasurementColumns: SpaceMeasurementDataColumns;
    listOfImages: { data: string; serialNumber: string }[];
    hideCharts?: boolean;
};

const IaqInsight2SensorSection = ({
    translationFunction,
    sensorType,
    sensorThresholdConfiguration,
    sensorReportData,
    spaceMeasurementColumns,
    listOfImages,
    hideCharts,
}: IaqInsight2SensorSectionProps): React.ReactElement => {
    const image = listOfImages.length > 0 && listOfImages.find(chart => chart.serialNumber === sensorType);
    const { ranges: thresholdRanges, unit: measurementUnit } = sensorThresholdConfiguration;
    const formattedSensorDisplayName = translationFunction(sensorFullNameKey(sensorType)).replace('CO₂', 'CO2');
    const formattedSensorDescription = translationFunction(`IAQReport2.${sensorType}Description`).replace(
        /CO₂/gi,
        'CO2'
    );
    const formattedMeasurementUnit = formatSensorUnit(sensorType, measurementUnit);

    const { averageValueWithinOpeningHours, averageValue: overallAverageValue, averageGraph } = sensorReportData;

    const hasSpacesWithData = Object.values(sensorReportData.devicesOpeningHoursValues || {}).some(
        spaceData =>
            spaceData.average !== undefined &&
            spaceData.average !== null &&
            !Number.isNaN(spaceData.average) &&
            spaceData.average !== 0
    );

    const hasGraphData = averageGraph && averageGraph.length > 0;

    const hasValidData = hasSpacesWithData || hasGraphData;

    const averageWithinOpeningHoursRating = hasValidData
        ? sensorRating(thresholdRanges, averageValueWithinOpeningHours)
        : undefined;
    const overallAverageValueRating = hasValidData ? sensorRating(thresholdRanges, overallAverageValue) : undefined;

    const spaceMeasurementValuesArray: DeviceMeasurement[] = Object.values(
        sensorReportData.devicesOpeningHoursValues || {}
    );
    const spacesGroupedByRating = calculateSpacesByRating(spaceMeasurementValuesArray, thresholdRanges);
    const spacesCountByIssueLevel = getSpacesWithIssues(spacesGroupedByRating);
    const peakMeasurementRangesByRating = calculatePeakRanges(spacesGroupedByRating);

    const sensorIconImageSource = getSensorIconSource(sensorType);
    const averageWithinOpeningHoursStatusColor = getStatusColor(averageWithinOpeningHoursRating || Rating.GOOD);
    const overallAverageStatusColor = getStatusColor(overallAverageValueRating || Rating.GOOD);

    return (
        <View>
            {/* Sensor Header: Icon + Name on left, Threshold pills on right */}
            <View style={iaqInsight2PdfStyles.sensorHeader} wrap={false}>
                <View style={iaqInsight2PdfStyles.sensorTitle}>
                    {sensorIconImageSource && (
                        <Image src={sensorIconImageSource} style={iaqInsight2PdfStyles.metricCardIcon} />
                    )}
                    <Text style={iaqInsight2PdfStyles.sensorName}>{formattedSensorDisplayName}</Text>
                </View>
                {sensorShowsRatingLabels(sensorType) && (
                    <View style={iaqInsight2PdfStyles.ratingLegend}>
                        {thresholdRanges
                            .filter(thresholdRange => formatRangeText(thresholdRange, measurementUnit) !== '')
                            .map(thresholdRange => {
                                const formattedThresholdRangeText = formatRangeText(thresholdRange, measurementUnit);
                                const statusPillColors = getStatusPillColors(thresholdRange.rating);

                                const thresholdKey = `threshold-${thresholdRange.from ?? 'min'}-${
                                    thresholdRange.to ?? 'max'
                                }-${thresholdRange.rating}`;

                                return (
                                    <View key={thresholdKey} style={iaqInsight2PdfStyles.legendItem}>
                                        <View
                                            style={[
                                                iaqInsight2PdfStyles.thresholdPill,
                                                { backgroundColor: statusPillColors.backgroundColor },
                                            ]}
                                        >
                                            <Text
                                                style={[
                                                    iaqInsight2PdfStyles.pillText,
                                                    { color: statusPillColors.textColor },
                                                ]}
                                            >
                                                {formatRating(thresholdRange.rating)}
                                            </Text>
                                        </View>
                                        <Text style={iaqInsight2PdfStyles.legendRange}>
                                            {formattedThresholdRangeText}
                                        </Text>
                                    </View>
                                );
                            })}
                    </View>
                )}
            </View>

            {/* Description */}
            <Text style={iaqInsight2PdfStyles.description}>{formattedSensorDescription}</Text>

            {/* Summary Cards: 2x2 Grid Layout */}
            <View style={iaqInsight2PdfStyles.summaryCardsGrid} wrap={false}>
                {/* Top Row: Average Cards */}
                {/* Average within opening hours */}
                <View style={iaqInsight2PdfStyles.summaryCard} wrap={false}>
                    <Text style={iaqInsight2PdfStyles.summaryCardTitle}>
                        {translationFunction('IAQReport2.AverageWithinOpeningHours')}
                    </Text>
                    <View style={iaqInsight2PdfStyles.summaryCardValue}>
                        <Text style={iaqInsight2PdfStyles.summaryValue}>
                            {hasValidData &&
                            averageValueWithinOpeningHours !== undefined &&
                            averageValueWithinOpeningHours !== null &&
                            !Number.isNaN(averageValueWithinOpeningHours)
                                ? formatSensorValue(averageValueWithinOpeningHours, measurementUnit)
                                : 'N/A'}
                        </Text>
                        {hasValidData &&
                            averageValueWithinOpeningHours !== undefined &&
                            averageValueWithinOpeningHours !== null &&
                            !Number.isNaN(averageValueWithinOpeningHours) && (
                                <Text style={iaqInsight2PdfStyles.summaryUnit}>{formattedMeasurementUnit}</Text>
                            )}
                    </View>
                    {sensorShowsRatingLabels(sensorType) && hasValidData && averageWithinOpeningHoursRating && (
                        <Text
                            style={[
                                iaqInsight2PdfStyles.summaryRating,
                                { color: averageWithinOpeningHoursStatusColor },
                            ]}
                        >
                            {formatRating(averageWithinOpeningHoursRating)}
                        </Text>
                    )}
                </View>

                {/* Average value */}
                <View style={iaqInsight2PdfStyles.summaryCard} wrap={false}>
                    <Text style={iaqInsight2PdfStyles.summaryCardTitle}>
                        {translationFunction('IAQReport2.AverageValueNoException')}
                    </Text>
                    <View style={iaqInsight2PdfStyles.summaryCardValue}>
                        <Text style={iaqInsight2PdfStyles.summaryValue}>
                            {hasValidData &&
                            overallAverageValue !== undefined &&
                            overallAverageValue !== null &&
                            !Number.isNaN(overallAverageValue)
                                ? formatSensorValue(overallAverageValue, measurementUnit)
                                : 'N/A'}
                        </Text>
                        {hasValidData &&
                            overallAverageValue !== undefined &&
                            overallAverageValue !== null &&
                            !Number.isNaN(overallAverageValue) && (
                                <Text style={iaqInsight2PdfStyles.summaryUnit}>{formattedMeasurementUnit}</Text>
                            )}
                    </View>
                    {sensorShowsRatingLabels(sensorType) && hasValidData && overallAverageValueRating && (
                        <Text style={[iaqInsight2PdfStyles.summaryRating, { color: overallAverageStatusColor }]}>
                            {formatRating(overallAverageValueRating)}
                        </Text>
                    )}
                </View>

                {sensorShowsRatingLabels(sensorType) &&
                    spacesCountByIssueLevel.poor > 0 &&
                    peakMeasurementRangesByRating.poor && (
                        <View style={iaqInsight2PdfStyles.detailCard} wrap={false}>
                            <View style={iaqInsight2PdfStyles.detailCardHeader}>
                                <View
                                    style={[iaqInsight2PdfStyles.coloredDot, { backgroundColor: colors.redPersian }]}
                                />
                                <Text style={iaqInsight2PdfStyles.detailCount}>
                                    {spacesCountByIssueLevel.poor === 1
                                        ? translationFunction('IAQReport2.SpaceCount', {
                                              count: spacesCountByIssueLevel.poor.toString(),
                                          })
                                        : translationFunction('IAQReport2.SpacesCount', {
                                              count: spacesCountByIssueLevel.poor.toString(),
                                          })}
                                </Text>
                            </View>
                            <Text style={iaqInsight2PdfStyles.detailLabel}>
                                {translationFunction('IAQReport2.WithPoorLevels')}
                            </Text>
                            <Text style={iaqInsight2PdfStyles.detailPeakRange}>
                                {translationFunction('IAQReport2.HighestLevelReported')
                                    .replace(/\{\{value\}\}/g, peakMeasurementRangesByRating.poor.max.toFixed(1))
                                    .replace(/\{\{unit\}\}/g, formattedMeasurementUnit)}
                            </Text>
                        </View>
                    )}

                {sensorShowsRatingLabels(sensorType) &&
                    spacesCountByIssueLevel.fair > 0 &&
                    peakMeasurementRangesByRating.fair && (
                        <View style={iaqInsight2PdfStyles.detailCard} wrap={false}>
                            <View style={iaqInsight2PdfStyles.detailCardHeader}>
                                <View
                                    style={[
                                        iaqInsight2PdfStyles.coloredDot,
                                        { backgroundColor: colors.yellowSunlight },
                                    ]}
                                />
                                <Text style={iaqInsight2PdfStyles.detailCount}>
                                    {spacesCountByIssueLevel.fair === 1
                                        ? translationFunction('IAQReport2.SpaceCount', {
                                              count: spacesCountByIssueLevel.fair.toString(),
                                          })
                                        : translationFunction('IAQReport2.SpacesCount', {
                                              count: spacesCountByIssueLevel.fair.toString(),
                                          })}
                                </Text>
                            </View>
                            <Text style={iaqInsight2PdfStyles.detailLabel}>
                                {translationFunction('IAQReport2.WithFairLevels')}
                            </Text>
                            <Text style={iaqInsight2PdfStyles.detailPeakRange}>
                                {translationFunction('IAQReport2.HighestLevelReported')
                                    .replace(/\{\{value\}\}/g, peakMeasurementRangesByRating.fair.max.toFixed(1))
                                    .replace(/\{\{unit\}\}/g, formattedMeasurementUnit)}
                            </Text>
                        </View>
                    )}

                {sensorShowsRatingLabels(sensorType) &&
                    spacesCountByIssueLevel.good > 0 &&
                    peakMeasurementRangesByRating.good && (
                        <View style={iaqInsight2PdfStyles.detailCard} wrap={false}>
                            <View style={iaqInsight2PdfStyles.detailCardHeader}>
                                <View
                                    style={[
                                        iaqInsight2PdfStyles.coloredDot,
                                        { backgroundColor: colors.greenBiosphere },
                                    ]}
                                />
                                <Text style={iaqInsight2PdfStyles.detailCount}>
                                    {spacesCountByIssueLevel.good === 1
                                        ? translationFunction('IAQReport2.SpaceCount', {
                                              count: spacesCountByIssueLevel.good.toString(),
                                          })
                                        : translationFunction('IAQReport2.SpacesCount', {
                                              count: spacesCountByIssueLevel.good.toString(),
                                          })}
                                </Text>
                            </View>
                            <Text style={iaqInsight2PdfStyles.detailLabel}>
                                {translationFunction('IAQReport2.WithNoIssues')}
                            </Text>
                            <Text style={iaqInsight2PdfStyles.detailPeakRange}>
                                {translationFunction('IAQReport2.HighestLevelReported')
                                    .replace(/\{\{value\}\}/g, peakMeasurementRangesByRating.good.max.toFixed(1))
                                    .replace(/\{\{unit\}\}/g, formattedMeasurementUnit)}
                            </Text>
                        </View>
                    )}
            </View>

            {/* Chart Image: Display if available and not hidden */}
            {!hideCharts && image && image.data && (
                <View style={iaqInsight2PdfStyles.chartContainer} wrap={false}>
                    <Image src={image.data} style={iaqInsight2PdfStyles.chartImage} />
                </View>
            )}

            {/* Data Table */}
            {hasSpacesWithData && spaceMeasurementColumns.length > 0 ? (
                <IaqInsight2DataTable
                    sensorType={sensorType}
                    translationFunction={translationFunction}
                    sensorThresholdConfiguration={sensorThresholdConfiguration}
                    spaceMeasurementColumns={spaceMeasurementColumns}
                />
            ) : (
                <View style={iaqInsight2PdfStyles.noDataContainer}>
                    <Text style={iaqInsight2PdfStyles.noDataText}>{translationFunction('IAQReport2.NoData')}</Text>
                </View>
            )}
        </View>
    );
};

export default IaqInsight2SensorSection;
